import { type ChangeEvent, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl, updateUser, type UserState } from '@/hooks/useAppStore';
import { useAppConfig } from '@/hooks/useAppConfig';
import { getMinPointsCost } from '@/api/config';
import { updateMe, uploadAvatar } from '@/api/user';

interface UserHeaderProps {
  user: UserState;
  onPointsClick: () => void;
  generationCount?: number;
}

export default function UserHeader({ user, onPointsClick, generationCount }: UserHeaderProps) {
  const navigate = useNavigate();
  const config = useAppConfig();
  const { t } = useTranslation();
  const minCost = getMinPointsCost(config);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editNickname, setEditNickname] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openEditModal = () => {
    setEditNickname(user.nickname);
    setAvatarPreview(null);
    setUploadedAvatarUrl(null);
    setShowEditModal(true);
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleAvatarSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isUploading) return;
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
    setIsUploading(true);
    try {
      const result = await uploadAvatar(file);
      setUploadedAvatarUrl(result.url);
      setAvatarPreview(result.url);
    } catch {
      showToast(t('profile_edit_avatarUploadFail'));
      setAvatarPreview(null);
      setUploadedAvatarUrl(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (isSaving || isUploading) return;
    const trimmed = editNickname.trim();
    if (!trimmed) return;
    setIsSaving(true);
    try {
      const payload: { nickname?: string; avatar?: string } = { nickname: trimmed };
      if (uploadedAvatarUrl) payload.avatar = uploadedAvatarUrl;
      await updateMe(payload);
      updateUser({ nickname: trimmed, ...(uploadedAvatarUrl ? { avatar: uploadedAvatarUrl } : {}) });
      setShowEditModal(false);
    } catch {
      showToast(t('profile_edit_saveFail'));
    } finally {
      setIsSaving(false);
    }
  };

  const currentAvatarInModal = avatarPreview ?? getAvatarUrl(user);

  return (
    <div className="relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #FF6B9D 0%, #C084FC 50%, #A855F7 100%)' }}>
      <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-15 bg-white"
        style={{ transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10 bg-white"
        style={{ transform: 'translate(-20%, 40%)' }} />

      <div className="relative z-10 pb-6 px-5" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}>
        {/* 顶部标题行 */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-lg font-bold text-white">{t('profile_title')}</h1>
          <button
            onClick={() => navigate('/settings')}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 cursor-pointer"
          >
            <i className="ri-settings-3-line text-white text-base"></i>
          </button>
        </div>

        {/* 用户信息 */}
        <div className="flex items-center gap-4 mb-5">
          <div className="w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-white/40 shrink-0">
            <img
              src={getAvatarUrl(user)}
              alt={user.nickname}
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-base font-bold text-white truncate">{user.nickname}</p>
                <p className="text-xs text-white/70 mt-0.5 truncate">{user.phone}</p>
              </div>
              {!user.isVip && (
                <button
                  onClick={() => navigate('/membership')}
                  className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line text-amber-300 text-xs"></i>
                  <span className="text-white text-[10px] font-semibold">{t('profile_openMembership')}</span>
                </button>
              )}
              {user.isVip && (
                <button
                  onClick={() => navigate('/membership')}
                  className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/30 cursor-pointer"
                >
                  <i className="ri-vip-crown-fill text-amber-300 text-xs"></i>
                  <span className="text-white text-[10px] font-semibold">VIP</span>
                </button>
              )}
            </div>
            <button
              onClick={openEditModal}
              className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-xl bg-white/20 backdrop-blur text-[10px] text-white font-medium border border-white/30 cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
            >
              <i className="ri-edit-line text-[10px]"></i>
              <span>{t('profile_editProfile')}</span>
            </button>
          </div>
        </div>

        {/* 数据统计行 */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t('profile_pointsBalance'), value: user.points, icon: 'ri-flashlight-fill', onClick: onPointsClick },
            { label: t('profile_generateTimes'), value: minCost ? Math.floor(user.points / minCost) : '--', icon: 'ri-sparkling-2-line', onClick: undefined },
            { label: t('profile_historyCount'), value: generationCount ?? 0, icon: 'ri-image-2-line', onClick: undefined },
          ].map((stat) => (
            <button
              key={stat.label}
              onClick={stat.onClick}
              className={`bg-white/15 rounded-2xl p-3 text-center ${stat.onClick ? 'cursor-pointer active:scale-95 transition-transform' : 'cursor-default'}`}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <i className={`${stat.icon} text-white/80 text-sm`}></i>
              </div>
              <p className="text-xl font-bold text-white">{stat.value}</p>
              <p className="text-xs text-white/60 mt-0.5">{stat.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* 编辑资料弹窗 */}
      {showEditModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-5 bg-black/40"
          onClick={() => !isSaving && !isUploading && setShowEditModal(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-800 text-center mb-5">{t('profile_edit_title')}</h3>

            {/* 头像 */}
            <div className="flex flex-col items-center mb-1">
              <div
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className="relative w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-gray-100 cursor-pointer active:scale-95 transition-transform"
              >
                <img src={currentAvatarInModal} alt="avatar" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  {isUploading
                    ? <i className="ri-loader-4-line text-white text-xl animate-spin"></i>
                    : <i className="ri-camera-line text-white text-xl opacity-80"></i>
                  }
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">{t('profile_edit_tapToChange')}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarSelect}
            />

            {/* 昵称 */}
            <p className="text-sm text-gray-500 mb-2 mt-4">{t('profile_edit_nickname')}</p>
            <input
              type="text"
              value={editNickname}
              onChange={(e) => setEditNickname(e.target.value)}
              placeholder={t('profile_edit_nicknamePlaceholder')}
              maxLength={20}
              className="w-full h-12 bg-gray-50 rounded-2xl px-4 text-sm text-gray-800 outline-none placeholder-gray-300 text-center mb-6"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
                className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {t('settings_cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isUploading || !editNickname.trim()}
                className="flex-1 h-12 rounded-2xl text-white font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                {isSaving ? t('profile_edit_saving') : t('profile_edit_save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 z-[70] -translate-x-1/2 px-4 py-2.5 rounded-2xl text-sm font-semibold text-white shadow-lg animate-slide-up"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', maxWidth: '80vw', textAlign: 'center' }}>
          {toast}
        </div>
      )}
    </div>
  );
}
